import * as React from 'react'

import { theme } from '@thg-commerce/gravity-theme'
import { GridItem } from '@thg-commerce/gravity-system'

import { ContentMastheadType } from './types'
import {
  StyledImage,
  StyledPicture,
  StyledGrid,
  StyledAmpContainer,
  TextGridItem,
  ImageGridItem,
  CategoryLink,
  ArticleTitle,
  ArticleSubHeading,
  Container,
  Description,
  UserBio,
  UserImage,
} from './style'

export interface ContentMastheadProps {
  isAmp?: boolean
  category?: {
    name: string
    url: string
  }
  title: string
  fallbackImageUrl?: string | undefined
  image?: {
    type: string
    imageUris: string[]
  } | null
  type: ContentMastheadType
  description?: string
  subHeading?: string
}

export const ContentMasthead = ({
  isAmp,
  category,
  title,
  subHeading,
  fallbackImageUrl,
  image,
  type,
  description,
}: ContentMastheadProps) => {
  const hasImage = image !== null
  const textGridItemConfig =
    type !== ContentMastheadType.INSET
      ? {
          colSpan: [12, 10, 8, 6],
          colStart: [1, 2, 3, 4],
          rowStart: [1, 1, 1, 1],
        }
      : {
          colSpan: [12, 10, 8, 4],
          colStart: [1, 2, 3, 3],
          rowStart: [1, 1, 1, 1],
        }
  const imageGridItemConfig =
    type === ContentMastheadType.USER
      ? {
          colSpan: [1, 1, 1, 1],
          colStart: [1, 1, 1, 1],
          rowStart: [1, 1, 1, 1],
        }
      : type !== ContentMastheadType.INSET
      ? {
          colSpan: [12, 12, 12, 12],
          colStart: [1, 1, 1, 1],
          rowStart: [1, 1, 1, 1],
        }
      : {
          colSpan: [12, 12, 12, 6],
          colStart: [1, 1, 1, 7],
          rowStart: [1, 1, 1, 1],
        }
  const elementProps = {
    hasImage,
    headerType: type,
    alignTextLeft:
      description && type === ContentMastheadType.HERO ? true : false,
  }

  const ampHeaderImageSize = (type: ContentMastheadType): string | null => {
    switch (type) {
      case ContentMastheadType.HERO:
        return '100vw'
      case ContentMastheadType.INSET:
        return `(min-width: ${theme.breakpointUtils.map.lg}px) 50vw, 100vw`
      default:
        return null
    }
  }

  return type === ContentMastheadType.USER ? (
    <StyledGrid {...elementProps}>
      <TextGridItem {...textGridItemConfig} {...elementProps}>
        <Container>
          <ArticleTitle {...elementProps}>{title}</ArticleTitle>
          {subHeading && (
            <ArticleSubHeading {...elementProps}>
              {subHeading}
            </ArticleSubHeading>
          )}
          <ImageGridItem {...imageGridItemConfig} {...elementProps}>
            <StyledPicture {...elementProps}>
              {image && (
                <source srcSet={image.imageUris.join(', ')} type={image.type} />
              )}
              <UserImage
                src={fallbackImageUrl}
                alt={title}
                crossOrigin="anonymous"
              />
            </StyledPicture>
          </ImageGridItem>

          {description && (
            <GridItem colSpan={[12, 6, 6, 4]}>
              <UserBio content={description} />
            </GridItem>
          )}
        </Container>
      </TextGridItem>
    </StyledGrid>
  ) : (
    <StyledGrid {...elementProps}>
      <TextGridItem {...textGridItemConfig} {...elementProps}>
        {type === ContentMastheadType.HERO ? (
          <Container>
            {category && (
              <CategoryLink
                sizing="regular"
                emphasis="low"
                renderedAs="a"
                href={category.url}
                {...elementProps}
              >
                {category.name}
              </CategoryLink>
            )}
            <ArticleTitle {...elementProps}>{title}</ArticleTitle>
            {description && (
              <Description {...elementProps}>{description}</Description>
            )}
          </Container>
        ) : (
          <React.Fragment>
            {category && (
              <CategoryLink
                sizing="regular"
                emphasis="low"
                renderedAs="a"
                href={category.url}
                {...elementProps}
              >
                {category.name}
              </CategoryLink>
            )}
            <ArticleTitle {...elementProps}>{title}</ArticleTitle>
            {description && (
              <Description {...elementProps}>{description}</Description>
            )}
          </React.Fragment>
        )}
      </TextGridItem>
      {type !== ContentMastheadType.TEXTONLY && (
        <ImageGridItem {...imageGridItemConfig} {...elementProps}>
          {isAmp ? (
            <StyledAmpContainer {...elementProps}>
              <amp-img
                alt={title}
                srcSet={image && image.imageUris.join(', ')}
                src={fallbackImageUrl}
                layout="fill"
                sizes={`${ampHeaderImageSize(type)}`}
              />
            </StyledAmpContainer>
          ) : (
            <StyledPicture {...elementProps}>
              {image && (
                <source srcSet={image.imageUris.join(', ')} type={image.type} />
              )}
              {fallbackImageUrl && (
                <StyledImage
                  src={fallbackImageUrl}
                  alt={title}
                  crossOrigin="anonymous"
                ></StyledImage>
              )}
            </StyledPicture>
          )}
        </ImageGridItem>
      )}
    </StyledGrid>
  )
}
